// What the admin match actions do, in words (audit A4/A5). Unit tested in
// tests/unit/match-actions.test.ts.
//
// Two very different actions used to share the label "Cancel schedule":
//   - generated matches (tournament draws, americano rotations) keep the match
//     and only lose their time → "Clear time" (DELETE fixtures/{id}/schedule);
//   - league and ladder matches are DELETED (DELETE fixtures/{id}), and for a
//     ladder the challenge goes too → "Delete match".
// Neither sends the players anything, so the confirms say so.

/** A match the draw/rotation owns: it can lose its time but never be deleted. */
export function isGeneratedMatch(fixture) {
    return !!(fixture?.tournamentStageId || fixture?.americanoCycleDivisionId || fixture?.americanoCycleId);
}

/** "Sam v Alex" (or "this match" when the teams aren't known yet). */
export function matchName(fixture) {
    const home = fixture?.homeTeam?.name || fixture?.homeTeam?.actualName;
    const away = fixture?.awayTeam?.name || fixture?.awayTeam?.actualName;
    if (home && away) return `${home} v ${away}`;
    return home || away || 'this match';
}

/** Confirm text for deleting an unplayed league / ladder match. */
export function deleteMatchMessage(fixture) {
    const name = matchName(fixture);
    if (fixture?.meetOccurrenceId) {
        const hasResult = fixture.homeTeamResult != null || fixture.awayTeamResult != null;
        return `${name} is removed from its date${hasResult ? ' with its result' : ''}. Nobody is emailed. This can't be undone.`;
    }
    if (fixture?.ladderChallengeId) {
        return `${name} will be removed and its ladder challenge cancelled too. To play, a new challenge is needed. Both players are emailed.`;
    }
    return `${name} will be removed, and the players will need to arrange it again. Both players are emailed.`;
}

/**
 * Can this viewer delete an UNPLAYED match (DELETE fixtures/{id})? Once a
 * ladder match is arranged only a club admin can (it cancels the challenge
 * too; the API refuses players) — players suggest another time instead.
 * Every other match: as before, anyone who can edit it, or an admin.
 */
export function canDeleteUnplayedMatch(fixture, {isAdmin = false, canEdit = false} = {}) {
    if (!fixture) return false;
    if (fixture.ladderChallengeId) return !!isAdmin;
    return !!(canEdit || isAdmin);
}

/** Confirm text for clearing the time of a generated match. */
export function clearTimeMessage(fixture) {
    return `The time and court are removed from ${matchName(fixture)}. The match stays in place, ready to be rescheduled. The players are emailed.`;
}

/** Where to go after the match whose own page you're on was deleted. */
export function afterDeleteRoute(fixture) {
    if (fixture?.ladderId) return `/ladders/${fixture.ladderId}`;
    if (fixture?.leagueId) {
        return fixture.leagueCycleId
            ? `/leagues/${fixture.leagueId}/${fixture.leagueCycleId}`
            : `/leagues/${fixture.leagueId}`;
    }
    return '/';
}

/**
 * A meet match is the club admin's to remove whatever its state, result and
 * all (owner, 11 Oct 2026): a wrong pairing or a night that never happened
 * should not sit in anyone's match list. Members clear a result instead (the
 * API refuses them). Mirrors the app's showMeetDeleteButton.
 */
export function canDeleteMeetMatch(fixture, {isAdmin = false} = {}) {
    return !!(fixture?.meetOccurrenceId && isAdmin);
}
